import { useContext } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { NotificationContext } from '../../../NotificationContext';
import { expressMutation } from '../../../repository/react-query/useExpress';

export const useMutateScheduleSupplyCell = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useContext(NotificationContext);

    return useMutation({
        mutationFn: expressMutation,
        onMutate: ({ id, body }) => {
            const previousDataMap = [];

            // iterate over all supply item queries
            queryClient
                .getQueryCache()
                .findAll(['list-view-supply-items'])
                .forEach((query) => {
                    const { queryKey } = query;
                    const oldData = queryClient.getQueryData(queryKey);

                    if (oldData) {
                        // store data before updating for rollback
                        previousDataMap.push({ queryKey, oldData });

                        // create a new optimistic version of the cached data
                        const newData = oldData.map((row) => {
                            const updatedItems = row?.items?.map((item) =>
                                item?._id === id
                                    ? {
                                          ...item,
                                          ...body,
                                      }
                                    : item
                            );

                            return {
                                ...row,
                                items: updatedItems,
                            };
                        });

                        // apply optimistic update to cache
                        queryClient.setQueryData(queryKey, newData);
                    }
                });

            // return previous state to onError for rollback
            return { previousDataMap };
        },
        onSuccess: (_response) => {
            queryClient.invalidateQueries(['list-view-schedule-totals']);

            showSnackbar({ severity: 'success', message: 'Success' });
        },
        onError: (error, _variables, context) => {
            // restore previous data from rollback snapshot
            if (context?.previousDataMap) {
                context.previousDataMap.forEach(({ queryKey, oldData }) => {
                    queryClient.setQueryData(queryKey, oldData);
                });
            }

            const message = error?.response?.data?.message;
            showSnackbar({ severity: 'error', message: message ?? 'Failed to update the schedule supply item. Changes reverted.' });
        },
    });
};

export const useMutateProcessorMatchCandidateCell = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useContext(NotificationContext);

    return useMutation({
        mutationFn: expressMutation,
        onMutate: ({ id, body, ...rest }) => {
            const previousDataMap = [];
            const matches = queryClient.getQueriesData({ queryKey: ['auto-match-candidates'] });

            matches.forEach(([key, cachedData]) => {
                if (!cachedData) return;

                const updatedData = cachedData.map((row) => {
                    const updatedItems = row?.items?.map((item) => (item?._id === id ? { ...item, ...body } : item));

                    return {
                        ...row,
                        items: updatedItems,
                    };
                });

                // Save snapshot
                previousDataMap.push({ queryKey: key, oldData: cachedData });

                // Optimistically update cache
                queryClient.setQueryData(key, updatedData);
            });

            return { previousDataMap };
        },
        onSuccess: (response) => {
            const id = response._id;
            const matches = queryClient.getQueriesData({ queryKey: ['auto-match-candidates'] });
            matches.forEach(([key, cachedData]) => {
                const hasItem = cachedData?.some((row) => row?.items?.some((item) => item?._id === id));
                if (hasItem) {
                    queryClient.invalidateQueries({ queryKey: key });
                }
            });

            queryClient.invalidateQueries(['auto-match-schedule-totals']);
            queryClient.invalidateQueries(['auto-match-allocation-totals']);
            showSnackbar({ severity: 'success', message: 'Success' });
        },
        onError: (error, _variables, context) => {
            if (context?.previousDataMap) {
                context.previousDataMap.forEach(({ queryKey, oldData }) => {
                    queryClient.setQueryData(queryKey, oldData);
                });
            }

            const message = error?.response?.data?.message;
            showSnackbar({
                severity: 'error',
                message: message ?? 'Failed to update the autopilot match candidate. Changes reverted.',
            });
        },
    });
};
